/**
 * Resolve the API base URL for customer-facing endpoints (/v1/open/*).
 *
 * Priority:
 *  1. VITE_API_URL – build-time override (useful for local dev / custom builds).
 *  2. Runtime detection based on hostname – the backend is mounted at
 *     different junction paths depending on the environment.
 */
function resolveOpenApiBaseUrl(): string {
  const buildTime = import.meta.env.VITE_API_URL;
  if (buildTime != null && buildTime !== '') return buildTime.replace(/\/$/, '');

  if (typeof window !== 'undefined') {
    const { hostname } = window.location;
    if (hostname.includes('mistralnet')) return '/befaringseksperten';
    if (hostname.includes('testgjensidige.no')) return '/befaringseksperten';
    if (hostname.includes('gjensidige.no')) return '/befaringseksperten';
  }

  // Local development — no prefix (the Vite dev-server proxy handles /v1/*).
  return '';
}

const API_BASE_URL = resolveOpenApiBaseUrl();

type RedirectResponse = Pick<Response, 'redirected' | 'url'>;

export function getAuthenticationRedirectUrl(
  response: RedirectResponse,
  returnUrl: string,
): string | null {
  if (!response.redirected) return null;

  try {
    const redirectUrl = new URL(response.url);
    const currentUrl = new URL(returnUrl);
    const operation = redirectUrl.searchParams.get('TAM_OP');

    if (
      redirectUrl.origin !== currentUrl.origin ||
      redirectUrl.pathname !== '/eai/op' ||
      (operation !== 'login' && operation !== 'stepup')
    ) {
      return null;
    }

    redirectUrl.searchParams.set('URL', returnUrl);
    return redirectUrl.toString();
  } catch {
    return null;
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  const authenticationRedirectUrl = getAuthenticationRedirectUrl(response, window.location.href);
  if (authenticationRedirectUrl) {
    window.location.assign(authenticationRedirectUrl);
    // Keep the request pending while the browser leaves the page, so callers do not render an API error.
    return new Promise<T>(() => undefined);
  }

  if (!response.ok) {
    let errBody = '';
    try {
      errBody = await response.text();
    } catch {
      /* ignore */
    }
    throw new ApiError(response.status, response.statusText, errBody);
  }

  // Read body as text first so we can transparently handle any empty 2xx
  // response (204 No Content, 201 Created with no body, etc.) without
  // tripping JSON.parse on an empty string.
  const text = await response.text();
  if (text.length === 0) {
    return undefined as T;
  }
  return JSON.parse(text) as T;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly body?: string,
  ) {
    super(message);
    this.name = 'ApiError';
    if (body) console.error(`[ApiError] ${status} ${message}:`, body);
  }
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),

  supplier: {
    getCase: (token: string) =>
      request<import('../types').SupplierCaseResponse>('GET', `/v1/supplier/cases/${token}`),
    submitOffer: (token: string, body: import('../types').OfferSubmissionRequest) =>
      request<void>('POST', `/v1/supplier/cases/${token}/offer`, body),
    denyCase: (token: string, body: import('../types').SupplierDenyRequest) =>
      request<void>('POST', `/v1/supplier/cases/${token}/deny`, body),
  },
};
